// @flow
import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    ButtonGroup,
    DividerNew as Divider,
    Notification,
    StyledModal,
    ModalActions,
    Spinner,
    Stack,
    Text,
    FlexRow,
    FlexCol,
    NumberField,
    Box,
} from 'sarsaparilla';

import { getProcessedErrorMessage } from '../utils/handleBackEndError';
import usePrevious from '../hooks/usePrevious';

import ErrorNotification, { type ErrorNotificationProps } from '../common/NewErrorNotification'

type GroupSizeTableProps = {
    groupSize: number,
    originalGroupSize: number,
    minGroupSize: number,
    maxGroupSize: number,
    onGroupSizeChange: (number) => void,
}

const GroupSizeTable = ({
    groupSize,
    originalGroupSize,
    minGroupSize,
    maxGroupSize,
    onGroupSizeChange,

}: GroupSizeTableProps) => {
    return (
        <Box border="gray">
            <FlexRow className="m-2">
                <FlexCol xs={7} sm={5}>
                    <Text size="lg" fontWeight="semibold">Original Group Size: </Text>
                </FlexCol>
                <FlexCol xs={5} className="pl-2">
                    <Text size="lg">
                        {originalGroupSize} {originalGroupSize > 1 ? 'members' : 'member'}
                    </Text>
                </FlexCol>

            </FlexRow>
            <Divider />
            <FlexRow className="m-2">
                <FlexCol xs={7} sm={5} className="mt-1">
                    <Text size="lg" fontWeight="semibold">Group Size Adjusted To:</Text>
                </FlexCol>
                <FlexCol xs={5}>
                    <NumberField
                        id="controlled-members"
                        units="members"
                        min={minGroupSize}
                        max={maxGroupSize}
                        value={groupSize}
                        onChange={onGroupSizeChange} />
                </FlexCol>
            </FlexRow>
        </Box>
    )
}

export type ModifyGroupSizeModalProps = $ReadOnly<{
    fetchMaxAvailableGroupSize: () => Promise<number>,
    isOpen?: boolean,
    isModifyingGroupSize: boolean,
    onRequestClose: () => void,
    onSuccess: (number) => void,
    originalGroupSize: number,
    permitRuleMinGroupSize?: number,
    modifyGroupSize: (number) => void | Promise<any>,
    modifyGroupSizeError?: string,
}>
export default function ModifyGroupSizeModal({
    fetchMaxAvailableGroupSize,
    isOpen = false,
    isModifyingGroupSize,
    onRequestClose,
    onSuccess,
    originalGroupSize,
    permitRuleMinGroupSize = 1,
    modifyGroupSize,
    modifyGroupSizeError,
}: ModifyGroupSizeModalProps) {
    const [fetchMaxAvailableGroupSizeError, setFetchMaxAvailableGroupSizeError] = useState();
    const [newGroupSize, setNewGroupSize] = useState<number>(originalGroupSize || 1)
    const prevIsOpen = usePrevious<boolean>(isOpen, false);
    const [maxGroupSizeAvailable, setMaxGroupSizeAvailable] = useState(0);
    const [isFetchingInitialContent, setIsFetchingInitialContent] = useState(true)
    const maxGroupSize = maxGroupSizeAvailable
    const minGroupSize = permitRuleMinGroupSize
    const isModificationsDisabled = (!isFetchingInitialContent && maxGroupSize === 0);

    useEffect(() => {
        setNewGroupSize(originalGroupSize || 1)
    }, [originalGroupSize])

    const getMaxAvailableGroupSize = useCallback(async () => {
        setIsFetchingInitialContent(true);
        try {
            const max = await fetchMaxAvailableGroupSize();
            setMaxGroupSizeAvailable(max);
        }
        catch(error) {
            setFetchMaxAvailableGroupSizeError(getProcessedErrorMessage(error.message))
        }
        finally {
            setIsFetchingInitialContent(false);
        }
    }, [setMaxGroupSizeAvailable, setIsFetchingInitialContent, fetchMaxAvailableGroupSize]);

    useEffect(() => {
        // toggled from false -> true
        if(!prevIsOpen && isOpen) {
            setNewGroupSize(originalGroupSize || 1)
            getMaxAvailableGroupSize()
        }
    }, [isOpen, prevIsOpen, getMaxAvailableGroupSize])

    const onSave = async () => {
        try {
            await modifyGroupSize(newGroupSize)
            onSuccess(newGroupSize);
        }
        catch {
            // no action needed, just to avoid raise to sentry.io
        }
    }

    const getErrorNotificationProps = (): ErrorNotificationProps => {
        if(fetchMaxAvailableGroupSizeError) {
            return {
                body: fetchMaxAvailableGroupSizeError,
                isShow: true,
                dismiss: undefined
            }
        }
        if(modifyGroupSizeError) {
            return {
                body: undefined,
                isShow: true,
                dismiss: undefined
            }
        }
        return {
            body: undefined,
            isShow: false,
            dismiss: undefined,
        }
    }

    const renderLoadingBody = () => (<Spinner isCentered size="xl" />)

    const renderMaxGroupSizeErrorBody = () => (
        <Stack>
            <ErrorNotification {...getErrorNotificationProps()} />

            <ModalActions>
                <ButtonGroup
                    isFullWidthOnMobile
                    isStretchedToFit>
                    <Button
                        appearance="tertiary"
                        onClick={onRequestClose}>
                        Cancel
                    </Button>
                </ButtonGroup>
            </ModalActions>
        </Stack>
    )

    const renderModificationDisabledBody = () => (
        <Stack>
            <ErrorNotification {...getErrorNotificationProps()} />

            <Text>
                Unfortunately, group size modifications are unavailable for this reservation.
                Please review the reservation modification policies for this facility or contact the permitting office for assistance.
            </Text>

            <ModalActions>
                <ButtonGroup
                    isFullWidthOnMobile
                    isStretchedToFit>
                    <Button
                        appearance="primary"
                        onClick={onRequestClose}>
                        Okay
                    </Button>
                </ButtonGroup>
            </ModalActions>
        </Stack>
    )

    const renderModifyGroupSizeBody = () => (
        <Stack>
            <ErrorNotification {...getErrorNotificationProps()} />

            <Text>
                You may increase your group size up to the maximum available.
                For your current reservation, the maximum available quota is {maxGroupSize} members.
            </Text>

            <Text fontStyle="italic">*Increasing your group size my incur additional fees based on the permit fee policies.</Text>

            <Divider />

            <Text>If you decrease your group size, released spots will become available to the public &
                <Text tag="strong" fontWeight="semibold"> may not be available to you in the future.</Text>
            </Text>

            <GroupSizeTable
                groupSize={newGroupSize}
                originalGroupSize={originalGroupSize || 0}
                minGroupSize={minGroupSize}
                maxGroupSize={maxGroupSize}
                onGroupSizeChange={setNewGroupSize} />

            {newGroupSize < originalGroupSize && (
                <Notification
                    type="warning"
                    title="Attention:">
                    <Text>
                        Decreasing the group size associated with your reservation will release previously reserved seats to the public.
                        This action is irreversible, and these seats may be unavailable to you in the future.
                        Select “Save Changes” if you would like to proceed.
                    </Text>
                </Notification>
            )}

            <ModalActions>
                <ButtonGroup
                    isFullWidthOnMobile
                    isStretchedToFit>
                    <Button
                        appearance="tertiary"
                        onClick={onRequestClose}>
                        Cancel
                    </Button>
                    <Button
                        isLoading={isModifyingGroupSize}
                        isDisabled={!newGroupSize || isModifyingGroupSize}
                        onClick={onSave}>
                        Save Changes
                    </Button>
                </ButtonGroup>
            </ModalActions>
        </Stack>
    )

    const renderBody = () => {
        if(isFetchingInitialContent) {
            return renderLoadingBody()
        }

        if(fetchMaxAvailableGroupSizeError) {
            return renderMaxGroupSizeErrorBody()
        }

        if(isModificationsDisabled) {
            return renderModificationDisabledBody()
        }

        return renderModifyGroupSizeBody()
    }

    return (
        <StyledModal
            shouldShowCloseButton
            shouldActionsStickInMobile
            size="md"
            isOpen={isOpen}
            heading={isModificationsDisabled ? "Modifications Unavailable" : "Modify Group Size"}
            onRequestClose={onRequestClose} >

            {renderBody()}

        </StyledModal>
    )
}
